import { type Chain, type DynamicClient } from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import {
  DYNAMIC_WAAS_METADATA,
  createWaasProvider,
  getAllUserWaasAddressesForChain,
} from '@dynamic-labs-sdk/client/waas/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';

import { evmConfirmTransaction } from '../../../utils/evmConfirmTransaction';
import { evmExecuteSwapTransaction } from '../../../utils/evmExecuteSwapTransaction';
import { evmTransferAmount } from '../../../utils/evmTransferAmount';
import type { WaasEvmWalletProvider } from '../../WaasEvmWalletProvider.types';
import { createViemWalletClientForWaas } from './createViemWalletClientForWaas';

type CreateWalletProviderForWaasEvmParams = {
  dynamicClient: DynamicClient;
};

/** @not-instrumented */
export const createWalletProviderForWaasEvm = ({
  dynamicClient,
}: CreateWalletProviderForWaasEvmParams): WaasEvmWalletProvider => {
  const chain: Chain = 'EVM';

  const walletProviderType = WalletProviderEnum.EmbeddedWallet;

  const key = formatWalletProviderKey({
    chain,
    displayName: DYNAMIC_WAAS_METADATA.displayName,
    walletProviderType,
  });

  const waasProvider = createWaasProvider({
    chain,
    sdkClient: dynamicClient,
  });

  const createViemWalletClient: WaasEvmWalletProvider['createViemWalletClient'] =
    async ({ walletAccount, httpTransportConfig, viemWalletClientConfig }) => {
      return createViemWalletClientForWaas(
        {
          httpTransportConfig,
          viemWalletClientConfig,
          waasProvider,
          walletAccount,
        },
        dynamicClient
      );
    };

  const getActiveNetworkId: WaasEvmWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  const getConnectedAddresses: WaasEvmWalletProvider['getConnectedAddresses'] =
    async () => {
      const waasEvmAddresses = getAllUserWaasAddressesForChain(
        { chain },
        dynamicClient
      );

      return {
        addresses: waasEvmAddresses,
      };
    };

  const signMessage: WaasEvmWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    return waasProvider.signMessage({
      message,
      walletAccount,
    });
  };

  const switchActiveNetwork: WaasEvmWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) =>
      switchActiveNetworkInLastKnownRegistry({
        client: dynamicClient,
        networkId,
        walletProviderKey: key,
      });

  const terminate: typeof waasProvider.terminate = async (args) => {
    return waasProvider.terminate(args);
  };

  return {
    ...waasProvider,
    chain,
    confirmTransaction: (args) => evmConfirmTransaction(args, dynamicClient),
    createViemWalletClient,
    executeSwapTransaction: (args) =>
      evmExecuteSwapTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(DYNAMIC_WAAS_METADATA.displayName),
    key,
    metadata: {
      displayName: DYNAMIC_WAAS_METADATA.displayName,
      icon: DYNAMIC_WAAS_METADATA.icon,
    },
    signMessage,
    switchActiveNetwork,
    terminate,
    transferAmount: (args) => evmTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
